import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tv, Cpu, Radio, BarChart3, ArrowRight } from "lucide-react";
import portfolio from "@/data/portfolio";

const icons = [Cpu, Tv, Radio, BarChart3];

export default function RideTV() {
  const d = portfolio.ridetv;
  return (
    <section
      id="ridetv"
      data-testid="ridetv-section"
      className="py-24 md:py-40 border-t hairline bg-black text-white"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-8 h-px bg-white/40" />
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
                05 · {d.eyebrow}
              </span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight text-white leading-[1.0]">
              {d.title}
            </h2>
            <p className="mt-6 text-lg md:text-xl text-gray-300 font-light leading-relaxed max-w-2xl">
              {d.headline}
            </p>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-3xl aspect-[4/3]"
            >
              <img
                src={d.image}
                alt="Ryde TV"
                className="w-full h-full object-cover opacity-90"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-5 left-5 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                RYDE TV · Studio
              </div>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
          {d.metrics.map((m, i) => {
            const Icon = icons[i % icons.length];
            return (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`ridetv-metric-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 md:p-7 backdrop-blur-sm hover:bg-white/[0.07] transition-colors"
              >
                <Icon className="w-5 h-5 text-white/70" />
                <div className="mt-4 font-serif-display text-4xl md:text-5xl font-light tracking-tight">
                  {m.value}
                </div>
                <div className="mt-2 text-sm text-gray-400">{m.label}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14">
          <h3 className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-400">
            Pillars
          </h3>
          <div className="mt-5 flex flex-wrap gap-2">
            {d.pillars.map((p) => (
              <span
                key={p}
                data-testid={`ridetv-pillar-${p.replace(/\s+/g, "-").toLowerCase()}`}
                className="rounded-full border border-white/15 px-5 py-2 text-sm text-gray-200 hover:border-white/40 hover:text-white transition-colors"
              >
                {p}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-12 md:mt-16">
          <Link
            to="/projects/ridetv"
            data-testid="ridetv-open-case-study"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-gray-100 rounded-full px-6 py-3 text-sm font-medium transition-colors"
          >
            View more <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
